import RightArrow from '@/images/arrows/RightArrow.svg';
import { PlanGroup, productToProductName, ProductType } from '@d-id/types';
import styled from '@emotion/styled';

import CloseWithCircle from '@/images/CloseWithCircle.svg';
import { DialogActions, DialogContent, DialogTitle, IconButton } from '@mui/material';

import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import CloneVoice from '@/images/CloneVoice.svg';
import Gallery from '@/images/Gallery.svg';
import UserGroup from '@/images/UserGroup.svg';
import { useAnalytics } from '@/services/analytics';
import theme from '@/theme';
import { toCamelCase } from '@/utils/utils';
import Button from '../Buttons/Button';
import { MaskedIcon } from '../MaskedIcon/MaskedIcon';

const Title = styled.div`
    margin-bottom: 0.5rem;
    padding-top: 14px;
    text-align: center;
    font-style: normal;
    font-weight: 700;
    font-size: 24px;
    line-height: 30px;
    letter-spacing: -0.02em;
`;

const Subtitle = styled.span`
    color: ${theme.colors.secondary.main};
    font-family: Manrope;
    font-size: 16px;
    font-style: normal;
    font-weight: 600;
    line-height: normal;
    letter-spacing: 0.15px;
    margin-block-start: 32px;
    align-self: flex-start;
`;

const ButtonText = styled.div`
    margin-right: 1rem;
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    line-height: 18px;
`;

const StyledDialogTitle = styled(DialogTitle)`
    font-size: 1.6rem;
    justify-content: center;
    flex-direction: column;
    margin-bottom: 10px;
`;

const StyledDialogContent = styled(DialogContent, { shouldForwardProp: p => p !== 'isSpinner' })<{ isSpinner: boolean }>(
    ({ isSpinner }) => ({
        minHeight: isSpinner ? '25rem' : '10rem',
        padding: '0 40px !important',
        transition: 'min-height 0.75s ease-in',
    })
);

const StyledIconButton = styled(IconButton)`
    position: absolute;
    right: 1rem;
    color: ${theme.colors.primary};
    top: 0.5rem;
`;

const StyledButton = styled(Button)({
    padding: '0.5rem 40px',
    width: '100%',
    height: '44px',
});

const CancelButton = styled(StyledButton)`
    &.MuiButtonBase-root {
        background-color: ${theme.colors.black[100]} !important;
    }

    &.MuiButtonBase-root:hover {
        background-color: ${theme.colors.black[200]} !important;
    }
`;

const StyledDialogActions = styled(DialogActions)`
    margin-top: 24px;
    margin-bottom: 2.5rem;
    padding: 0 40px !important;
    display: flex;
    flex-direction: column;
    gap: 8px;
`;

const productName = (planName: ProductType | PlanGroup) => toCamelCase(productToProductName[planName]);

const BeforeAfterRowContainer = styled.div`
    display: flex;
    align-items: center;
    flex-direction: row;
`;

const BeforeText = styled.span`
    color: ${theme.colors.black[700]};
    font-family: Manrope;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: 0.15px;
    text-decoration-line: line-through;
    margin-inline: 8px;
`;

const AfterText = styled.span<{ red?: boolean }>`
    color: ${({ red, theme }) => (red ? '#ed254e' : theme.colors.secondary.main)};
    font-family: Manrope;
    font-size: 16px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
    letter-spacing: 0.15px;
`;

const Description = styled.span`
    color: ${({ theme }) => theme.colors.secondary.main};
    text-align: center;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: normal;
    letter-spacing: 0.15px;
`;

const BeforeAfterContainer = styled.div`
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-block-start: 16px;
    align-self: flex-start;
`;

const BeforeAfterRow = ({
    beforeText,
    beforeIcon,
    afterText,
}: {
    beforeText: string;
    beforeIcon: string;
    afterText: string | React.ReactNode;
}) => {
    return (
        <BeforeAfterRowContainer>
            <MaskedIcon src={beforeIcon} size="24px" color={theme.colors.black[700]} />
            <BeforeText>{beforeText}</BeforeText>
            <MaskedIcon src={RightArrow} size="24px" color={theme.colors.black[700]} style={{ marginInlineEnd: '4px' }} />
            {typeof afterText === 'string' ? <AfterText red>{afterText}</AfterText> : afterText}
        </BeforeAfterRowContainer>
    );
};

const AdvancedToPro = () => {
    return (
        <BeforeAfterContainer>
            <BeforeAfterRow
                beforeText="5 Express Avatars"
                beforeIcon={UserGroup}
                afterText={
                    <AfterText>
                        <AfterText red>{3}</AfterText> Express Avatars
                    </AfterText>
                }
            />
            <BeforeAfterRow
                beforeText="5 Voice Clones"
                beforeIcon={CloneVoice}
                afterText={
                    <AfterText>
                        <AfterText red>{3}</AfterText> Voice Clones
                    </AfterText>
                }
            />
            <BeforeAfterRow beforeText="Custom logos" beforeIcon={Gallery} afterText="AI Watermarks" />
        </BeforeAfterContainer>
    );
};

const AdvancedToLite = () => {
    return (
        <BeforeAfterContainer>
            <BeforeAfterRow
                beforeText="5 Express Avatars"
                beforeIcon={UserGroup}
                afterText={
                    <AfterText>
                        <AfterText red>{1}</AfterText> Express Avatars
                    </AfterText>
                }
            />

            <BeforeAfterRow
                beforeText="5 Voice Clones"
                beforeIcon={CloneVoice}
                afterText={
                    <AfterText>
                        <AfterText red>{3}</AfterText> Voice Clones
                    </AfterText>
                }
            />

            <BeforeAfterRow beforeText="Premium Voices" beforeIcon={CloneVoice} afterText="Standard Voices" />
            <BeforeAfterRow beforeText="Premium Avatars" beforeIcon={UserGroup} afterText="Standard Avatars" />
            <BeforeAfterRow beforeText="Custom logos" beforeIcon={Gallery} afterText="AI Watermarks" />
        </BeforeAfterContainer>
    );
};

const ProToLite = () => {
    return (
        <BeforeAfterContainer>
            <BeforeAfterRow
                beforeText="3 Express Avatars"
                beforeIcon={UserGroup}
                afterText={
                    <AfterText>
                        <AfterText red>{1}</AfterText> Express Avatars
                    </AfterText>
                }
            />
            <BeforeAfterRow beforeText="3 Premium+ Avatars" beforeIcon={UserGroup} afterText="No Premium+ Avatars" />
            <BeforeAfterRow beforeText="3 Voice Clones" beforeIcon={CloneVoice} afterText="No voice clones" />
            <BeforeAfterRow beforeText="Premium Avatars" beforeIcon={UserGroup} afterText="Standard Avatars" />
            <BeforeAfterRow beforeText="Premium Voices" beforeIcon={CloneVoice} afterText="Standard Voices" />
            <BeforeAfterRow beforeText="AI Watermark" beforeIcon={Gallery} afterText="D-ID watermark" />
        </BeforeAfterContainer>
    );
};

const PlansToContent: Record<string, React.FC> = {
    [`${PlanGroup.ADVANCED}-${PlanGroup.PRO}`]: AdvancedToPro,
    [`${PlanGroup.ADVANCED}-${PlanGroup.LITE}`]: AdvancedToLite,
    [`${PlanGroup.PRO}-${PlanGroup.LITE}`]: ProToLite,
};

const DowngradeContent = ({ newPlan, oldPlan }: { newPlan: PlanGroup; oldPlan: PlanGroup }) => {
    const Content = PlansToContent[`${oldPlan}-${newPlan}`] || (() => null);
    return <Content />;
};

interface DowngradeModalProps {
    newPlan: PlanGroup;
    oldPlan: PlanGroup;
    onConfirm: () => void;
    onClose: () => void;
}

export const DowngradeModal = ({ onConfirm, onClose, newPlan, oldPlan }: DowngradeModalProps) => {
    const mixpanel = useAnalytics();

    const mixpanelProps = { event: 'click', plan_source: oldPlan, plan_target: newPlan };
    const handleDowngrade = () => {
        mixpanel?.track('downgrade-plan', mixpanelProps);
        onConfirm();
    };
    const handleKeepPlan = () => {
        mixpanel?.track('keep-plan', mixpanelProps);
        onClose();
    };

    return (
        <BootstrapDialog
            onClose={onClose}
            open
            width="450px"
            contentstyle={{ flex: 1, overflow: 'auto', alignItems: 'center' }}
            PaperProps={{ style: { borderRadius: '12px' } }}>
            <StyledDialogTitle id="downgrade-plan-modal">
                <Title>Downgrade plan</Title>
                <StyledIconButton aria-label="close" onClick={onClose}>
                    <MaskedIcon src={CloseWithCircle} size="38px" />
                </StyledIconButton>
            </StyledDialogTitle>
            <StyledDialogContent isSpinner={false}>
                <Description>
                    By downgrading, you’ll be losing access to powerful features that may impact your experience once your current billing
                    cycle ends
                </Description>
                <Subtitle>Benefits you will lose</Subtitle>
                <DowngradeContent newPlan={newPlan} oldPlan={oldPlan} />
            </StyledDialogContent>
            <StyledDialogActions disableSpacing>
                <CancelButton onClick={handleKeepPlan} aria-label="cancel">
                    <ButtonText>Keep current plan</ButtonText>
                </CancelButton>
                <StyledButton onClick={handleDowngrade} aria-label="downgrade">
                    <ButtonText>Downgrade to {productName(newPlan)}</ButtonText>
                </StyledButton>
            </StyledDialogActions>
        </BootstrapDialog>
    );
};
